import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";

/* ═══════════════════════════════════════════════════════════════════════════
   CaciUI — the shared design system
   ───────────────────────────────────────────────────────────────────────────
   One source of truth for the "Comptoir" look: tokens, motion, and the parts
   every page repeats (header band, section heading, skeletons, empty states,
   pagination, filter bar).

   WHY THIS FILE EXISTS
   The homepage carries its whole design system inline. Copying that into the
   twelve remaining pages would mean thirteen copies of the same 1,200 lines —
   thirteen places to edit when a colour changes, and thirteen chances for the
   pages to drift apart. Everything reusable lives here instead.

   HOW TO USE IT
     import { CaciStyles, PageHeader, SectionHead, ... } from "../components/CaciUI";

     export default function SomePage() {
       return (
         <div className={`caci-page${isRtl ? " is-rtl" : ""}`} dir={...}>
           <CaciStyles />
           <PageHeader title="..." crumbs={[...]} />
           ...
         </div>
       );
     }

   The root element must carry `caci-page` — every rule below is scoped to it,
   so nothing here can leak into the Bootstrap theme the rest of the site uses.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Scroll reveal ───────────────────────────────────────────────────────────
   Elements opt in with data-rv="rise|fade|left|right|zoom" and stagger via
   style={{ "--i": n }}. Any failure reveals everything immediately: content
   must never be left invisible because an observer threw. */
export function useReveal(deps = []) {
  const scopeRef = useRef(null);

  useEffect(() => {
    const scope = scopeRef.current || document;
    let io;
    const showAll = (els) => els.forEach((el) => el.classList.add("rv-in"));

    try {
      const els = Array.from(scope.querySelectorAll("[data-rv]:not(.rv-in)"));
      if (!els.length) return;

      const reduce =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (reduce || typeof IntersectionObserver === "undefined") {
        showAll(els);
        return;
      }

      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("rv-in");
            io.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -6% 0px", threshold: 0.06 }
      );
      els.forEach((el) => io.observe(el));
    } catch (e) {
      showAll(Array.from((scopeRef.current || document).querySelectorAll("[data-rv]")));
    }

    return () => io && io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return scopeRef;
}

/* ── Reading progress: a hairline of brass across the top of the viewport ── */
export function useScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    let raf = 0;
    const read = () => {
      raf = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setPct(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(read); };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return pct;
}

/* ── Debounce ────────────────────────────────────────────────────────────────
   Every listing page types into a search box. 400ms is the point where the
   list feels live without firing a request per keystroke. */
export function useDebounced(value, delay = 400) {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setOut(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return out;
}

/* ── The signature mark ──────────────────────────────────────────────────────
   A chamber of commerce is the body that stamps and seals documents. This is
   that stamp, drawn in strokes: two rings, a ring of ticks, a centre dot. */
export function Seal({ size = 22, className = "", spin = false }) {
  const ticks = [];
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2;
    const r1 = 26, r2 = i % 2 === 0 ? 21 : 23.5;
    ticks.push(
      <line
        key={i}
        x1={32 + Math.cos(a) * r1}
        y1={32 + Math.sin(a) * r1}
        x2={32 + Math.cos(a) * r2}
        y2={32 + Math.sin(a) * r2}
      />
    );
  }
  return (
    <svg
      className={`seal${spin ? " seal-spin" : ""} ${className}`}
      width={size} height={size} viewBox="0 0 64 64" fill="none"
      aria-hidden="true" focusable="false"
    >
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="32" cy="32" r="30" opacity=".45" />
        <circle cx="32" cy="32" r="17" />
        <g opacity=".8">{ticks}</g>
      </g>
      <circle cx="32" cy="32" r="5" fill="currentColor" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE STYLESHEET
   Injected once per page by <CaciStyles/>. Every selector is scoped to
   .caci-page so this cannot touch the Bootstrap theme used elsewhere.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CACI_CSS = `
.caci-page {
  /* ── Colour ─────────────────────────────────────────────── */
  --ink:      #08150E;
  --ink-2:    #12281C;
  --pine:     #0F4A30;
  --leaf:     #1A7A4A;
  --leaf-2:   #22A060;
  --brass:    #B08A2E;
  --brass-2:  #D8B455;
  --paper:    #FAFBFA;
  --mint:     #E9F3ED;
  --surface:  #FFFFFF;
  --txt:      #0B1F16;
  --muted:    #5C7268;
  --rule:     rgba(15,74,48,.13);
  --rule-2:   rgba(15,74,48,.26);
  --danger:   #A33A2B;

  /* ── Type ───────────────────────────────────────────────── */
  --display: 'Bricolage Grotesque', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --body:    'Instrument Sans', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --mono:    'JetBrains Mono', ui-monospace, 'SFMono-Regular', monospace;

  /* ── Motion ─────────────────────────────────────────────── */
  --e:    cubic-bezier(.22,1,.36,1);
  --e-io: cubic-bezier(.65,.05,.36,1);
  --t1:   .26s;
  --t2:   .62s;

  /* ── Rhythm ─────────────────────────────────────────────── */
  --pad: clamp(52px, 7.5vw, 104px);
  --gut: clamp(18px, 4.4vw, 40px);

  position: relative;
  font-family: var(--body);
  color: var(--txt);
  background: var(--paper);
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: 60vh;
}
.caci-page *, .caci-page *::before, .caci-page *::after { box-sizing: border-box; }
.caci-page img { max-width: 100%; }

/* Arabic has no Bricolage; one family covers all three roles. */
.caci-page.is-rtl {
  --display: 'IBM Plex Sans Arabic', 'Bricolage Grotesque', system-ui, sans-serif;
  --body:    'IBM Plex Sans Arabic', 'Instrument Sans', system-ui, sans-serif;
}

.caci-shell {
  width: 100%; max-width: 1280px;
  margin-inline: auto; padding-inline: var(--gut);
}
.caci-section { padding-block: var(--pad); }

/* ── Reading progress ─────────────────────────────────────────────────── */
.caci-page .caci-progress {
  position: fixed; inset-block-start: 0; inset-inline: 0;
  height: 2px; z-index: 1200; pointer-events: none;
  background: linear-gradient(90deg, var(--leaf), var(--brass-2));
  transform-origin: left center;
  transition: transform .12s linear, opacity .3s ease;
}
.caci-page.is-rtl .caci-progress { transform-origin: right center; }

/* ── Reveal on scroll ─────────────────────────────────────────────────── */
.caci-page [data-rv] {
  opacity: 0;
  transition: opacity var(--t2) var(--e), transform var(--t2) var(--e),
              filter var(--t2) var(--e);
  transition-delay: calc(var(--i, 0) * 55ms);
  will-change: opacity, transform;
}
.caci-page [data-rv="rise"]  { transform: translateY(24px); }
.caci-page [data-rv="fade"]  { transform: none; }
.caci-page [data-rv="left"]  { transform: translateX(-28px); }
.caci-page [data-rv="right"] { transform: translateX(28px); }
.caci-page [data-rv="zoom"]  { transform: scale(.95); filter: blur(5px); }
.caci-page [data-rv].rv-in { opacity: 1; transform: none; filter: none; will-change: auto; }

/* ── The seal ─────────────────────────────────────────────────────────── */
.caci-page .seal { flex: 0 0 auto; display: block; }
.caci-page [data-rv] .seal {
  transform: scale(1.8) rotate(-14deg); opacity: 0;
  transition: transform .7s var(--e), opacity .4s ease;
}
.caci-page [data-rv].rv-in .seal { transform: none; opacity: 1; }
@keyframes sealSpin { to { transform: rotate(360deg); } }
.caci-page .seal-spin { animation: sealSpin 14s linear infinite; }

/* ═══════════════════════════════════════════════════════════════════════
   PAGE HEADER
   The homepage opens on a full-bleed carousel. An inner page must not
   compete with that, so it gets a compact engraved band instead: the same
   guilloché as the services section, the seal, and a mono breadcrumb that
   reads like a file reference.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-page .pg-head {
  position: relative; overflow: hidden; isolation: isolate;
  background: var(--ink); color: #fff;
  padding-block: clamp(48px, 9vw, 96px) clamp(40px, 7vw, 76px);
  margin-top: 150px;
}
.caci-page .pg-head::before {
  content: ''; position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background:
    repeating-linear-gradient(114deg, rgba(216,180,85,.075) 0 1px, transparent 1px 9px),
    repeating-linear-gradient(-114deg, rgba(34,160,96,.07) 0 1px, transparent 1px 9px);
  -webkit-mask-image: radial-gradient(120% 92% at 50% 0%, #000 16%, transparent 78%);
          mask-image: radial-gradient(120% 92% at 50% 0%, #000 16%, transparent 78%);
}
/* An oversized seal, mostly cropped by the band — the impression of a stamp
   pressed half off the edge of a document. */
.caci-page .pg-head-seal {
  position: absolute; z-index: 0; pointer-events: none;
  inset-block-start: 50%; inset-inline-end: -70px;
  transform: translateY(-50%);
  color: rgba(216,180,85,.13);
  display: none;
}
@media (min-width: 768px) { .caci-page .pg-head-seal { display: block; } }
/* Lift the content above the guilloché — but NOT the decorative seal, which
   must stay absolutely positioned. This rule and .pg-head-seal have equal
   specificity, so targeting the universal selector here made position:relative win by source
   order: the seal dropped into normal flow and inflated the band to 575px. */
.caci-page .pg-head > .caci-shell { position: relative; z-index: 1; }

.caci-page .pg-crumbs {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  margin: 0 0 16px; padding: 0; list-style: none;
  font-family: var(--mono); font-size: 11px; letter-spacing: .1em;
  text-transform: uppercase;
}
.caci-page .pg-crumbs li { display: inline-flex; align-items: center; gap: 8px; }
.caci-page .pg-crumbs li + li::before {
  content: '/'; color: rgba(216,180,85,.55);
}
.caci-page .pg-crumbs a {
  position: relative;
  color: rgba(255,255,255,.62); text-decoration: none;
  padding-block: 6px; transition: color var(--t1) var(--e);
}
/* An 11px mono link is a 24px tap target — the bare minimum. The pseudo
   element lifts the real hit area to 40px without changing the layout. */
.caci-page .pg-crumbs a::after { content: ''; position: absolute; inset: -9px -6px; }
.caci-page .pg-crumbs a:hover { color: var(--brass-2); }
.caci-page .pg-crumbs .is-current { color: var(--brass-2); }
/* On a narrow screen a long trail wrapped to a second line that began with a
   stray separator. Keep it on one line and let the current item ellipsis —
   the ancestors are the part you navigate by, the current one you can read
   from the heading directly below it. */
@media (max-width: 575.98px) {
  .caci-page .pg-crumbs { flex-wrap: nowrap; }
  .caci-page .pg-crumbs li { min-width: 0; }
  .caci-page .pg-crumbs li:not(:last-child) { flex: 0 0 auto; }
  .caci-page .pg-crumbs li:last-child { flex: 0 1 auto; overflow: hidden; }
  .caci-page .pg-crumbs li:last-child .is-current {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;
  }
}

.caci-page .pg-title {
  font-family: var(--display);
  font-size: clamp(2rem, 6.4vw, 3.6rem);
  font-weight: 700; letter-spacing: -.034em; line-height: 1.02;
  margin: 0; color: #fff; text-wrap: balance; max-width: 22ch;
}
.caci-page .pg-sub {
  margin: 14px 0 0; max-width: 56ch;
  font-size: clamp(14.5px, 1.6vw, 16.5px); line-height: 1.7;
  color: rgba(255,255,255,.6);
}
.caci-page .pg-count {
  display: inline-flex; align-items: center; gap: 9px; margin-block-start: 20px;
  font-family: var(--mono); font-size: 11.5px; letter-spacing: .1em;
  text-transform: uppercase; color: var(--brass-2);
}
.caci-page .pg-count b { font-weight: 700; color: #fff; font-variant-numeric: tabular-nums; }

/* ── Section heading ──────────────────────────────────────────────────── */
.caci-page .sec-head { display: grid; gap: 18px; margin-bottom: clamp(28px, 4vw, 48px); }
.caci-page .sec-head-text { min-width: 0; }
.caci-page .sec-label {
  display: inline-flex; align-items: center; gap: 9px;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: .18em; text-transform: uppercase; color: var(--brass);
  margin-bottom: 12px;
}
.caci-page .sec-title {
  font-family: var(--display);
  font-size: clamp(1.7rem, 5.4vw, 2.7rem);
  font-weight: 700; letter-spacing: -.03em; line-height: 1.05;
  color: var(--ink); margin: 0; text-wrap: balance;
}
.caci-page .sec-sub {
  margin: 12px 0 0; max-width: 56ch;
  font-size: clamp(14.5px, 1.5vw, 16px); line-height: 1.72; color: var(--muted);
}
@media (min-width: 768px) {
  .caci-page .sec-head { grid-template-columns: 1fr auto; align-items: end; gap: 32px; }
  .caci-page .sec-head-action { justify-self: end; }
}

/* ── Buttons ──────────────────────────────────────────────────────────── */
.caci-page .btn-quiet {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  min-height: 48px; padding: 0 24px;
  border: 1px solid var(--rule-2); border-radius: 999px;
  font-family: var(--mono); font-size: 12px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  color: var(--pine); text-decoration: none; background: transparent; cursor: pointer;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-page .btn-quiet i { transition: transform var(--t1) var(--e); }
.caci-page .btn-quiet:hover {
  background: var(--pine); border-color: var(--pine); color: #fff; transform: translateY(-2px);
}
.caci-page .btn-quiet:hover i { transform: translateX(4px); }
.caci-page.is-rtl .btn-quiet:hover i { transform: translateX(-4px); }
.caci-page .btn-quiet:active { transform: scale(.97); }
.caci-page .btn-solid {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  min-height: 50px; padding: 0 28px; border: 0; border-radius: 999px; cursor: pointer;
  background: linear-gradient(180deg, var(--leaf-2), var(--leaf)); color: #fff;
  font-family: var(--body); font-size: 15px; font-weight: 600; text-decoration: none;
  box-shadow: 0 14px 30px -14px rgba(26,122,74,.9);
  transition: transform var(--t1) var(--e);
}
.caci-page .btn-solid:hover { color: #fff; transform: translateY(-2px); }
.caci-page .btn-solid:active { transform: translateY(0) scale(.98); }

/* ── Grid ─────────────────────────────────────────────────────────────── */
.caci-page .caci-grid {
  display: grid; gap: clamp(14px, 2.2vw, 24px);
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 272px), 1fr));
}
.caci-page .caci-grid > * { min-width: 0; }

/* ═══════════════════════════════════════════════════════════════════════
   ARTICLE CARD — shared by the homepage, the archive, search and category
   ═══════════════════════════════════════════════════════════════════════ */
.caci-page .art-card {
  position: relative; display: flex; flex-direction: column;
  height: 100%; min-height: 340px;
  border-radius: 18px; overflow: hidden;
  background: var(--surface); border: 1px solid var(--rule);
  box-shadow: 0 4px 18px -12px rgba(8,21,14,.3);
  transition: transform var(--t2) var(--e), box-shadow var(--t2) var(--e),
              border-color var(--t1) var(--e);
}
.caci-page .art-card:hover {
  transform: translateY(-8px);
  border-color: rgba(176,138,46,.4);
  box-shadow: 0 38px 64px -34px rgba(15,74,48,.45);
}
.caci-page .art-media {
  position: relative; height: 188px; flex: 0 0 auto; overflow: hidden; background: var(--mint);
}
.caci-page .art-media img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 1s var(--e);
}
.caci-page .art-card:hover .art-media img { transform: scale(1.06); }
.caci-page .art-media-ph {
  width: 100%; height: 100%; display: grid; place-items: center;
  background: linear-gradient(140deg, var(--mint), var(--paper));
  color: var(--rule-2); font-size: 30px;
}
.caci-page .art-date {
  position: absolute; z-index: 2; inset-block-end: 12px; inset-inline-start: 12px;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 999px;
  background: rgba(8,21,14,.62); color: #fff;
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  font-family: var(--mono); font-size: 10.5px; letter-spacing: .04em;
}
.caci-page .art-date i { color: var(--brass-2); font-size: 11px; }
/* Type badge — colour-coded so a mixed result set is scannable at a glance. */
.caci-page .art-kind {
  position: absolute; z-index: 2; inset-block-start: 12px; inset-inline-start: 12px;
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 28px; padding: 0 12px; border-radius: 999px;
  background: rgba(255,255,255,.94); color: var(--pine);
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  box-shadow: 0 4px 14px -6px rgba(8,21,14,.5);
  font-family: var(--mono); font-size: 10px; font-weight: 700;
  letter-spacing: .12em; text-transform: uppercase;
}
.caci-page .art-kind i { font-size: 11px; }
.caci-page .art-kind--article { color: var(--pine); }
.caci-page .art-kind--page    { color: #7A5A12; }
.caci-page .art-kind--service { color: #14544C; }

.caci-page .art-body { flex: 1; display: flex; flex-direction: column; padding: 16px 20px 20px; }
.caci-page .art-cats { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.caci-page .art-cat {
  position: relative; z-index: 2;
  display: inline-flex; align-items: center; min-height: 30px;
  padding: 4px 13px; border-radius: 999px;
  background: var(--mint); color: var(--pine); text-decoration: none;
  font-family: var(--mono); font-size: 10px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  transition: background var(--t1) var(--e), color var(--t1) var(--e);
}
.caci-page .art-cat:hover { background: var(--pine); color: #fff; }
.caci-page .art-title {
  font-family: var(--display); font-size: 16.5px; font-weight: 700;
  letter-spacing: -.02em; line-height: 1.34; color: var(--ink);
  text-decoration: none; margin: 0; display: block;
  transition: color var(--t1) var(--e);
}
/* The clamp lives on the span: overflow:hidden on the link would clip the
   stretched hit area below back down to the text box. */
.caci-page .art-title > span {
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-page .art-card:hover .art-title { color: var(--pine); }
/* The whole card opens the article, not just the line of title text. */
.caci-page .art-title::after { content: ''; position: absolute; inset: 0; z-index: 1; }
.caci-page .art-excerpt {
  margin: 10px 0 0; font-size: 13.5px; line-height: 1.65; color: var(--muted);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-page .art-foot {
  display: flex; align-items: center; gap: 8px; margin-top: auto; padding-top: 16px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: var(--leaf);
}
.caci-page .art-foot i { transition: transform var(--t1) var(--e); }
.caci-page .art-card:hover .art-foot i { transform: translateX(4px); }
.caci-page.is-rtl .art-card:hover .art-foot i { transform: translateX(-4px); }

/* ═══════════════════════════════════════════════════════════════════════
   DIRECTORY CARD — partners, chambers, and any other logo-led listing

   A logo is not a photograph. Cropping it to fill a tile (object-fit: cover,
   as the article card does) cuts the mark in half; letterboxing it into a
   plain white box makes the grid look broken. So the logo sits contained on
   a tinted plinth with real breathing room, and the plinth — not the logo —
   carries the hover state.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-page .dir-card {
  position: relative; display: flex; flex-direction: column;
  height: 100%; min-height: 300px;
  border-radius: 18px; overflow: hidden;
  background: var(--surface); border: 1px solid var(--rule);
  box-shadow: 0 4px 18px -12px rgba(8,21,14,.3);
  transition: transform var(--t2) var(--e), box-shadow var(--t2) var(--e),
              border-color var(--t1) var(--e);
}
.caci-page .dir-card:hover {
  transform: translateY(-8px);
  border-color: rgba(176,138,46,.42);
  box-shadow: 0 38px 64px -34px rgba(15,74,48,.45);
}
.caci-page .dir-plinth {
  position: relative; flex: 0 0 auto;
  height: 168px; padding: 24px;
  display: grid; place-items: center; overflow: hidden;
  background: linear-gradient(160deg, var(--mint), var(--paper));
  border-block-end: 1px solid var(--rule);
}
.caci-page .dir-plinth::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(rgba(15,74,48,.07) 1px, transparent 1px);
  background-size: 18px 18px;
  -webkit-mask-image: radial-gradient(ellipse 74% 74% at 50% 50%, transparent 34%, #000 100%);
          mask-image: radial-gradient(ellipse 74% 74% at 50% 50%, transparent 34%, #000 100%);
  opacity: .8;
}
.caci-page .dir-plinth img {
  position: relative; z-index: 1;
  max-width: 100%; max-height: 120px;
  width: auto; height: auto; object-fit: contain;
  transition: transform var(--t2) var(--e);
}
.caci-page .dir-card:hover .dir-plinth img { transform: scale(1.05) translateY(-2px); }
.caci-page .dir-plinth-ph {
  position: relative; z-index: 1;
  width: 62px; height: 62px; border-radius: 18px;
  display: grid; place-items: center;
  background: var(--pine); color: #fff; font-size: 26px;
  box-shadow: 0 14px 30px -14px rgba(15,74,48,.9);
}
.caci-page .dir-body {
  flex: 1; display: flex; flex-direction: column;
  padding: 18px 20px 20px; min-width: 0;
}
.caci-page .dir-title {
  font-family: var(--display); font-size: 16.5px; font-weight: 700;
  letter-spacing: -.02em; line-height: 1.3; color: var(--ink);
  margin: 0; text-decoration: none; display: block;
  transition: color var(--t1) var(--e);
}
.caci-page .dir-title > span {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-page .dir-card:hover .dir-title { color: var(--pine); }
/* The whole card is the target, not just the title text — but only when
   there is somewhere to go. */
.caci-page .dir-title::after { content: ''; position: absolute; inset: 0; z-index: 1; }
.caci-page .dir-title.is-static { font-size: 16.5px; }
.caci-page .dir-title.is-static::after { content: none; }
.caci-page .dir-card.is-static:hover {
  transform: none; border-color: var(--rule);
  box-shadow: 0 4px 18px -12px rgba(8,21,14,.3);
}
.caci-page .dir-card.is-static:hover .dir-title { color: var(--ink); }
.caci-page .dir-card.is-static:hover .dir-plinth img { transform: none; }
.caci-page .dir-desc {
  margin: 10px 0 0; font-size: 13.5px; line-height: 1.65; color: var(--muted);
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-page .dir-foot {
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: auto; padding-top: 16px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: var(--leaf);
}
.caci-page .dir-foot i { transition: transform var(--t1) var(--e); }
.caci-page .dir-card:hover .dir-foot i { transform: translate(3px, -3px); }
.caci-page .dir-card:hover .dir-foot.is-internal i { transform: translateX(4px); }
.caci-page.is-rtl .dir-card:hover .dir-foot.is-internal i { transform: translateX(-4px); }

/* Skeleton twin of the directory card. */
.caci-page .skel-dir {
  display: flex; flex-direction: column; height: 100%; min-height: 300px;
  border-radius: 18px; overflow: hidden;
  border: 1px solid var(--rule); background: var(--surface);
}
.caci-page .skel-dir > .skel-plinth { height: 168px; border-radius: 0; }

/* ═══════════════════════════════════════════════════════════════════════
   FILTER BAR
   ═══════════════════════════════════════════════════════════════════════ */
.caci-page .bar {
  position: sticky; inset-block-start: 0; z-index: 40;
  padding-block: 14px;
  background: rgba(250,251,250,.88);
  -webkit-backdrop-filter: blur(14px) saturate(150%);
          backdrop-filter: blur(14px) saturate(150%);
  border-block-end: 1px solid var(--rule);
}
.caci-page .bar-inner { display: grid; gap: 10px; }
/* Short selects sit side by side rather than stacked: three full-width rows
   of filters pushed the first result most of a screen down on a phone.
   auto-flow rather than a fixed 1fr 1fr so the row adapts to however many
   filters a page has — one filter fills the width instead of leaving a gap. */
.caci-page .bar-filters {
  display: grid; gap: 10px;
  grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr);
}
@media (min-width: 768px) {
  .caci-page .bar-inner { grid-template-columns: minmax(0, 1fr) auto; align-items: center; }
  .caci-page .bar-filters { display: flex; }
}
.caci-page .field {
  position: relative; display: flex; align-items: center;
  min-height: 52px; border-radius: 999px;
  background: var(--surface); border: 1px solid var(--rule);
  transition: border-color var(--t1) var(--e), box-shadow var(--t1) var(--e);
}
.caci-page .field:focus-within {
  border-color: var(--leaf); box-shadow: 0 0 0 4px rgba(26,122,74,.1);
}
.caci-page .field > i {
  flex: 0 0 auto; padding-inline: 18px 0; color: var(--muted); font-size: 15px;
}
.caci-page .field input {
  flex: 1; min-width: 0; height: 50px; padding-inline: 12px;
  border: 0; background: transparent; outline: none;
  font-family: var(--body); font-size: 15px; color: var(--txt);
}
.caci-page .field input::placeholder { color: var(--muted); opacity: .8; }
.caci-page .field-clear {
  flex: 0 0 auto; width: 40px; height: 40px; margin-inline-end: 6px;
  border: 0; border-radius: 50%; background: var(--mint); color: var(--pine);
  display: grid; place-items: center; cursor: pointer; font-size: 12px;
  transition: background var(--t1) var(--e), color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-page .field-clear:hover { background: var(--pine); color: #fff; transform: rotate(90deg); }
.caci-page .field-busy {
  flex: 0 0 auto; width: 16px; height: 16px; margin-inline-end: 18px;
  border-radius: 50%; border: 2px solid var(--rule); border-top-color: var(--leaf);
  animation: sealSpin .7s linear infinite;
}

.caci-page .select {
  position: relative; display: flex; align-items: center;
  min-height: 52px; border-radius: 999px;
  background: var(--surface); border: 1px solid var(--rule);
}
.caci-page .select:focus-within { border-color: var(--leaf); box-shadow: 0 0 0 4px rgba(26,122,74,.1); }
.caci-page .select select {
  flex: 1; min-width: 0; height: 50px;
  padding-inline: clamp(12px, 3.6vw, 20px) 38px;
  text-overflow: ellipsis;
  border: 0; background: transparent; outline: none; cursor: pointer;
  font-family: var(--mono); font-size: 12px; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase; color: var(--pine);
  -webkit-appearance: none; appearance: none;
}
/* Physical borders on purpose: a dropdown caret points DOWN in every
   language. With logical borders, border-inline-end flips to the left edge
   in RTL and the rotated caret ends up pointing sideways. */
.caci-page .select::after {
  content: ''; position: absolute; inset-inline-end: 18px; pointer-events: none;
  width: 7px; height: 7px;
  border-right: 2px solid var(--pine); border-bottom: 2px solid var(--pine);
  transform: translateY(-2px) rotate(45deg);
}

/* ═══════════════════════════════════════════════════════════════════════
   PAGINATION
   The original rendered <a> tags with a disabled attribute, which does
   nothing on an anchor — the arrows stayed clickable at both ends. These
   are real buttons that are really disabled.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-page .pager {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center;
  gap: 8px; padding: 0; margin: 0; list-style: none;
}
.caci-page .pager button {
  min-width: 46px; height: 46px; padding-inline: 12px;
  border-radius: 999px; cursor: pointer;
  border: 1px solid var(--rule); background: var(--surface); color: var(--txt);
  display: inline-flex; align-items: center; justify-content: center;
  font-family: var(--mono); font-size: 13px; font-weight: 600;
  font-variant-numeric: tabular-nums;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-page .pager button:hover:not(:disabled):not(.is-current) {
  border-color: var(--rule-2); background: var(--mint); color: var(--pine);
}
.caci-page .pager button:active:not(:disabled) { transform: scale(.94); }
.caci-page .pager button:disabled { opacity: .32; cursor: not-allowed; }
.caci-page .pager button.is-current {
  background: var(--pine); border-color: var(--pine); color: #fff;
  box-shadow: 0 12px 24px -14px rgba(15,74,48,.9);
}
.caci-page .pager .pager-gap {
  min-width: 24px; text-align: center; color: var(--muted);
  font-family: var(--mono); font-size: 13px; user-select: none;
}
.caci-page .pager-note {
  margin: 16px 0 0; text-align: center;
  font-family: var(--mono); font-size: 11px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--muted);
}
.caci-page .pager-note b { color: var(--pine); font-variant-numeric: tabular-nums; }
@media (max-width: 575.98px) {
  /* Numbers past the neighbours are noise on a 390px screen; the arrows and
     the "page 3 of 12" line carry the same information in less space. */
  .caci-page .pager .pager-num.is-far { display: none; }
  .caci-page .pager .pager-gap { display: none; }
}

/* ═══════════════════════════════════════════════════════════════════════
   SECTION STATES — skeleton, empty, error
   ═══════════════════════════════════════════════════════════════════════ */
.caci-page .skel {
  display: block; position: relative; overflow: hidden;
  background: var(--mint); border-radius: 8px; flex: 0 0 auto;
}
.caci-page .skel::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
  animation: skelSweep 1.45s var(--e-io) infinite;
}
.caci-page.is-rtl .skel::after { animation-name: skelSweepRtl; }
@keyframes skelSweep    { to { transform: translateX(100%);  } }
@keyframes skelSweepRtl { to { transform: translateX(-200%); } }

.caci-page .skel-card {
  display: flex; flex-direction: column; height: 100%; min-height: 340px;
  border-radius: 18px; overflow: hidden;
  border: 1px solid var(--rule); background: var(--surface);
}
.caci-page .skel-card > .skel-media { height: 188px; border-radius: 0; }
.caci-page .skel-card-body { padding: 16px 20px 20px; display: grid; gap: 10px; }

.caci-page .sec-note {
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: clamp(40px, 8vw, 72px) 24px; text-align: center;
  border: 1px dashed var(--rule-2); border-radius: 18px;
  background: rgba(15,74,48,.022);
  color: var(--muted); font-size: 14.5px; line-height: 1.6;
}
.caci-page .sec-note h3 {
  margin: 0; font-family: var(--display); font-size: clamp(1.15rem, 3.4vw, 1.5rem);
  font-weight: 700; letter-spacing: -.022em; color: var(--ink);
}
.caci-page .sec-note p { margin: 0; max-width: 44ch; }
.caci-page .sec-note .seal { color: var(--rule-2); }
.caci-page .sec-note.is-error .seal { color: rgba(163,58,43,.4); }

/* ── Accessibility & motion ───────────────────────────────────────────── */
.caci-page a:focus-visible,
.caci-page button:focus-visible,
.caci-page select:focus-visible,
.caci-page input:focus-visible,
.caci-page [tabindex]:focus-visible {
  outline: 3px solid var(--brass); outline-offset: 3px; border-radius: 8px;
}
.caci-sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
@media (hover: none) {
  .caci-page .art-card:hover { transform: none; box-shadow: inherit; }
}
@media (prefers-reduced-motion: reduce) {
  .caci-page *, .caci-page *::before, .caci-page *::after {
    animation-duration: .01ms !important; animation-iteration-count: 1 !important;
    transition-duration: .01ms !important; scroll-behavior: auto !important;
  }
  .caci-page [data-rv] { opacity: 1 !important; transform: none !important; filter: none !important; }
  .caci-page .skel::after { animation: none !important; }
}
`;

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

/* Injects the stylesheet once. React dedupes identical <style> children per
   tree, and a second copy on the page would be harmless anyway — but keeping
   it in one component means a token only ever changes in one place. */
export function CaciStyles() {
  return <style>{CACI_CSS}</style>;
}

/* ── Page header band ────────────────────────────────────────────────────────
   crumbs: [{ label, to }] — the last entry renders as plain current text.
   count:  optional { value, label } shown as a mono record line. */
export function PageHeader({ title, sub, crumbs = [], count, children }) {
  return (
    <header className="pg-head">
      <Seal size={280} className="pg-head-seal" spin />
      <div className="caci-shell">
        {crumbs.length > 0 && (
          <nav aria-label="Breadcrumb">
            <ol className="pg-crumbs" data-rv="fade">
              {crumbs.map((c, i) => {
                const last = i === crumbs.length - 1;
                return (
                  <li key={i}>
                    {last || !c.to ? (
                      <span className="is-current" aria-current={last ? "page" : undefined}>
                        {c.label}
                      </span>
                    ) : (
                      <Link to={c.to}>{c.label}</Link>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        )}
        <h1 className="pg-title" data-rv="rise" style={{ "--i": 1 }} dir="auto">
          {title}
        </h1>
        {sub && (
          <p className="pg-sub" data-rv="rise" style={{ "--i": 2 }} dir="auto">
            {sub}
          </p>
        )}
        {count && (
          <p className="pg-count" data-rv="rise" style={{ "--i": 3 }} aria-live="polite">
            <Seal size={14} />
            <b>{count.value}</b> {count.label}
          </p>
        )}
        {children}
      </div>
    </header>
  );
}

/* ── Section heading ─────────────────────────────────────────────────────────
   Drops the eyebrow when it would only repeat the title — three sections on
   the old homepage printed the same i18n key twice for exactly that reason. */
export function SectionHead({ label, title, sub, action }) {
  const norm = (s) => String(s || "").trim().toLowerCase();
  const showLabel = label && norm(label) !== norm(title);
  return (
    <header className="sec-head">
      <div className="sec-head-text">
        {showLabel && (
          <span className="sec-label" data-rv="fade">
            <Seal size={15} />
            {label}
          </span>
        )}
        <h2 className="sec-title" data-rv="rise" style={{ "--i": 1 }} dir="auto">{title}</h2>
        {sub && <p className="sec-sub" data-rv="rise" style={{ "--i": 2 }} dir="auto">{sub}</p>}
      </div>
      {action && (
        <div className="sec-head-action" data-rv="rise" style={{ "--i": 3 }}>{action}</div>
      )}
    </header>
  );
}

/* ── Skeletons ─────────────────────────────────────────────────────────────── */
export function Skel({ w, h, r, className = "", style }) {
  return (
    <span
      className={`skel ${className}`}
      style={{ width: w, height: h, borderRadius: r, ...style }}
      aria-hidden="true"
    />
  );
}

export function SkelCard() {
  return (
    <div className="skel-card" aria-hidden="true">
      <Skel className="skel-media" />
      <div className="skel-card-body">
        <Skel h={13} r={7} w="88%" />
        <Skel h={13} r={7} w="64%" />
        <Skel h={11} r={6} w="42%" style={{ marginBlockStart: 6 }} />
      </div>
    </div>
  );
}

export function SkelGrid({ count = 6 }) {
  return (
    <div className="caci-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => <SkelCard key={i} />)}
    </div>
  );
}

/* ── Empty / error ───────────────────────────────────────────────────────────
   These read differently on purpose. An empty result is a normal outcome and
   should stay calm. A failed request is not, and needs the one action that
   fixes it — otherwise the visitor is left reloading the whole page. */
export function SectionNote({ state = "empty", title, text, action }) {
  const isError = state === "error";
  return (
    <div className={`sec-note${isError ? " is-error" : ""}`} role="status">
      <Seal size={26} />
      {title && <h3>{title}</h3>}
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

/* ── Pagination ──────────────────────────────────────────────────────────────
   Windowed page numbers with real <button disabled> at the ends. On phones
   the far numbers collapse and the "page N of M" line carries the position
   instead, so the control never wraps onto three rows. */
export function Pager({ page, totalPages, onChange, labels = {} }) {
  if (!totalPages || totalPages < 2) return null;

  const L = {
    first: "First", prev: "Previous", next: "Next", last: "Last",
    page: "Page", of: "of", ...labels,
  };

  const span = 5;
  let start = Math.max(1, page - Math.floor(span / 2));
  let end = Math.min(start + span - 1, totalPages);
  if (end - start + 1 < span) start = Math.max(1, end - span + 1);

  const nums = [];
  for (let i = start; i <= end; i++) nums.push(i);

  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };

  return (
    <nav aria-label="Pagination">
      <ul className="pager">
        <li>
          <button type="button" onClick={() => go(1)} disabled={page === 1} aria-label={L.first}>
            <i className="bi bi-chevron-double-left" />
          </button>
        </li>
        <li>
          <button type="button" onClick={() => go(page - 1)} disabled={page === 1} aria-label={L.prev}>
            <i className="bi bi-chevron-left" />
          </button>
        </li>

        {start > 1 && <li className="pager-gap" aria-hidden="true">…</li>}

        {nums.map((n) => (
          <li key={n}>
            <button
              type="button"
              onClick={() => go(n)}
              className={`pager-num${n === page ? " is-current" : ""}${Math.abs(n - page) > 1 ? " is-far" : ""}`}
              aria-label={`${L.page} ${n}`}
              aria-current={n === page ? "page" : undefined}
            >
              {n}
            </button>
          </li>
        ))}

        {end < totalPages && <li className="pager-gap" aria-hidden="true">…</li>}

        <li>
          <button type="button" onClick={() => go(page + 1)} disabled={page === totalPages} aria-label={L.next}>
            <i className="bi bi-chevron-right" />
          </button>
        </li>
        <li>
          <button type="button" onClick={() => go(totalPages)} disabled={page === totalPages} aria-label={L.last}>
            <i className="bi bi-chevron-double-right" />
          </button>
        </li>
      </ul>
      <p className="pager-note">
        {L.page} <b>{page}</b> {L.of} <b>{totalPages}</b>
      </p>
    </nav>
  );
}

/* ── Article card ────────────────────────────────────────────────────────────
   One component for the archive, search, and category pages, so a change to
   how an article is presented lands everywhere at once. */
export function ArticleCard({
  to, image, title, excerpt, date, categories = [], badge,
  readLabel, isRtl, index = 0,
}) {
  return (
    <div data-rv="rise" style={{ "--i": index % 6 }}>
      <article className="art-card">
        <div className="art-media">
          {image ? (
            <img src={image} alt="" loading="lazy" decoding="async"
                 onError={(e) => {
                   e.currentTarget.style.display = "none";
                   e.currentTarget.parentElement.classList.add("no-img");
                 }} />
          ) : (
            <div className="art-media-ph"><i className="bi bi-journal-text" /></div>
          )}
          {date && (
            <span className="art-date">
              <i className="bi bi-calendar3" />
              {date}
            </span>
          )}
          {/* A search result set mixes articles, pages and services. Without a
              badge they all look identical and the visitor cannot tell what
              they are about to open. */}
          {badge && (
            <span className={`art-kind art-kind--${badge.kind}`}>
              {badge.icon && <i className={`bi ${badge.icon}`} />}
              {badge.label}
            </span>
          )}
        </div>
        <div className="art-body">
          {categories.length > 0 && (
            <div className="art-cats">
              {categories.map((c, i) => (
                <Link key={c.id ?? i} to={c.to} className="art-cat">{c.label}</Link>
              ))}
            </div>
          )}
          <Link to={to} className="art-title" dir="auto"><span>{title}</span></Link>
          {excerpt && <p className="art-excerpt" dir="auto">{excerpt}</p>}
          <span className="art-foot">
            {readLabel}
            <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
          </span>
        </div>
      </article>
    </div>
  );
}

/* ── Directory card ──────────────────────────────────────────────────────────
   Partners and chambers are the same shape: a logo, a name, sometimes a
   blurb, and one way onward. `href` makes it an external link (partners),
   `to` an internal route (chambers) — never both. */
export function DirectoryCard({ to, href, logo, title, description, actionLabel, isRtl, index = 0 }) {
  const external = Boolean(href);
  const linked = external || Boolean(to);

  /* Not every entry has somewhere to go. A card with no destination renders
     as a plain tile — no stretched hit area, no hover lift, no arrow — rather
     than a link pointing back at the page you are already on. */
  const titleNode = external ? (
    <a className="dir-title" dir="auto" href={href} target="_blank" rel="noopener noreferrer">
      <span>{title}</span>
    </a>
  ) : to ? (
    <Link to={to} className="dir-title" dir="auto"><span>{title}</span></Link>
  ) : (
    <h3 className="dir-title is-static" dir="auto"><span>{title}</span></h3>
  );

  return (
    <div data-rv="rise" style={{ "--i": index % 6 }}>
      <article className={`dir-card${linked ? "" : " is-static"}`}>
        <div className="dir-plinth">
          {logo ? (
            <img
              src={logo} alt="" loading="lazy" decoding="async"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <span className="dir-plinth-ph"><i className="bi bi-buildings" /></span>
          )}
        </div>
        <div className="dir-body">
          {titleNode}
          {description && <p className="dir-desc" dir="auto">{description}</p>}
          {linked && actionLabel && (
            <span className={`dir-foot${external ? "" : " is-internal"}`}>
              {actionLabel}
              <i className={external
                ? `bi bi-arrow-up-${isRtl ? "left" : "right"}`
                : `bi bi-arrow-${isRtl ? "left" : "right"}`} />
            </span>
          )}
        </div>
      </article>
    </div>
  );
}

export function SkelDirGrid({ count = 6 }) {
  return (
    <div className="caci-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skel-dir">
          <Skel className="skel-plinth" />
          <div className="skel-card-body">
            <Skel h={14} r={7} w="82%" />
            <Skel h={11} r={6} w="46%" style={{ marginBlockStart: 6 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Shared text utilities ───────────────────────────────────────────────────
   Every page reimplemented these. One copy avoids the drift where the
   homepage truncated titles at 60 characters and the archive at 50. */
export function stripHtml(raw) {
  if (!raw) return "";
  const div = document.createElement("div");
  div.innerHTML = raw;
  return div.innerText || div.textContent || "";
}

export function decodeEntities(text) {
  if (!text) return "";
  let out = text;
  const ta = document.createElement("textarea");
  let i = 0;
  do {
    ta.innerHTML = out;
    out = ta.value;
    i++;
  } while (out.includes("&") && i < 10);
  return out;
}

export function truncate(text, max) {
  if (!text) return "";
  const clean = decodeEntities(String(text)).trim();
  return clean.length > max ? clean.slice(0, max).trimEnd() + "…" : clean;
}

export function excerptOf(raw, words = 22) {
  if (!raw) return "";
  const text = decodeEntities(stripHtml(raw)).trim();
  if (!text) return "";
  const parts = text.split(/\s+/);
  return parts.length > words ? parts.slice(0, words).join(" ") + "…" : text;
}

/* Locale-aware date. Note the Arabic locale: the original used "ar-AR", which
   is not a real tag — browsers fall back to the default locale, so Arabic
   visitors were shown French or English dates. "ar-DZ" is the correct one. */
export function formatDate(dateString, lang, fallback = "") {
  if (!dateString) return fallback;
  const d = new Date(dateString);
  if (isNaN(d)) return fallback;
  const locale = { en: "en-US", ar: "ar-DZ", fr: "fr-FR" }[lang] || "fr-FR";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/* Language-prefixed path: French is the default and carries no prefix. */
export function langPath(lang, path) {
  return lang === "fr" ? path : `/${lang}${path}`;
}

/* Picks the field for the active language, falling back to the base column. */
export function localized(item, field, lang) {
  if (!item) return "";
  if (lang === "ar") return item[`${field}_ar`] || item[field] || "";
  if (lang === "en") return item[`${field}_en`] || item[field] || "";
  return item[field] || "";
}

export function localizedImage(item, field, lang, baseUrl) {
  if (!item) return null;
  let url;
  if (lang === "ar") url = item[`${field}_ar`] || item[field];
  else if (lang === "en") url = item[`${field}_en`] || item[field];
  else url = item[field];
  return url ? `${baseUrl}${url}` : null;
}

/* Fold case and strip diacritics for searching. Without this, typing "bejaia"
   fails to match "Béjaïa" and the directory looks empty — a real problem for
   Algerian place names, which are full of accents. */
export function normalize(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function matches(haystack, needle) {
  const n = normalize(needle);
  if (!n) return true;
  return normalize(haystack).includes(n);
}

/* ── usePagedList ────────────────────────────────────────────────────────────
   Four pages on this site are the same thing: a paginated list with a search
   box and a per-page selector, backed by an endpoint that returns
   { data, pagination }. Each one had its own copy of the debounce, the cancel
   token, the page-reset rule and the fetch — four copies that had already
   drifted apart (one truncated titles at 50 characters, another at 60; one
   reset the page on a new search, another did not).

   This hook owns that behaviour once. What each page still decides for itself
   is what a result looks like — which is the part that should differ.

   Filters live in the URL, so a filtered view can be shared, reloaded, and
   walked back through with the browser's Back button.

   `select` adapts the response shape: /api/pages returns
   { data, pagination } at the top level, while /api/categoryportail/edit/:id
   nests it under `articles` and carries the category alongside it. */
export function usePagedList({
  url,
  select,
  extraParams,
  perPageOptions = [6, 12, 24, 48],
  defaultPerPage = 12,
  searchKey = "q",
  apiSearchKey = "search",
  debounceMs = 400,
  enabled = true,
  scrollOffset = 90,
}) {
  const [params, setParams] = useSearchParams();

  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const perPage = perPageOptions.includes(Number(params.get("per")))
    ? Number(params.get("per"))
    : defaultPerPage;
  const query = params.get(searchKey) || "";

  const [term, setTerm] = useState(query);
  const debounced = useDebounced(term, debounceMs);

  const [items, setItems] = useState([]);
  const [extra, setExtra] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [state, setState] = useState(enabled ? "loading" : "idle");
  const [refetching, setRefetching] = useState(false);

  const cancelRef = useRef(null);
  const alive = useRef(true);
  const firstRun = useRef(true);
  const listRef = useRef(null);

  /* Held in refs so a caller passing an inline function or object literal
     does not retrigger the fetch on every render. */
  const selectRef = useRef(select);
  selectRef.current = select;
  const extraRef = useRef(extraParams);
  extraRef.current = extraParams;

  /* Serialised, so a caller can pass { status } inline without the identity
     of that object restarting the request each render. */
  const extraKey = JSON.stringify(extraParams || {});

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  /* The settled term goes into the URL. Page resets: staying on page 7 of a
     different result set shows an empty screen. `replace` keeps history from
     filling up with one entry per keystroke. */
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (debounced === query) return;
    const next = new URLSearchParams(params);
    if (debounced) next.set(searchKey, debounced); else next.delete(searchKey);
    next.delete("page");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const patch = useCallback((changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    if (resetPage) next.delete("page");
    setParams(next);
  }, [params, setParams]);

  const load = useCallback(() => {
    if (cancelRef.current) cancelRef.current.cancel("superseded");

    if (!enabled) {
      setItems([]); setTotal(0); setTotalPages(1);
      setState("idle"); setRefetching(false);
      return Promise.resolve();
    }

    cancelRef.current = axios.CancelToken.source();

    /* "refetching" rather than "loading" once there is already a list on
       screen: replacing a full page of results with a spinner on every
       keystroke is what made the originals feel like they were thrashing. */
    setState((s) => (s === "ready" ? s : "loading"));
    setRefetching(true);

    return axios
      .get(url, {
        params: {
          page,
          per_page: perPage,
          [apiSearchKey]: query,
          ...(extraRef.current || {}),
        },
        cancelToken: cancelRef.current.token,
      })
      .then((res) => {
        if (!alive.current) return;
        const picked = selectRef.current
          ? selectRef.current(res.data)
          : { items: res.data?.data, pagination: res.data?.pagination };

        const list = Array.isArray(picked?.items) ? picked.items : [];
        const pg = picked?.pagination || {};
        setItems(list);
        setExtra(picked?.extra ?? null);
        setTotal(Number(pg.total ?? list.length) || 0);
        setTotalPages(Math.max(1, Number(pg.last_page) || 1));
        setState("ready");
      })
      .catch((err) => {
        if (axios.isCancel(err) || !alive.current) return;
        console.error(`usePagedList: request to ${url} failed —`, err);
        setState("error");
      })
      .finally(() => {
        if (alive.current) setRefetching(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, page, perPage, query, extraKey, enabled, apiSearchKey]);

  useEffect(() => {
    load();
    return () => cancelRef.current && cancelRef.current.cancel("unmounted");
  }, [load]);

  /* Paging returns the viewport to the top of the results, not the top of the
     document — nobody wants to scroll past the header again on every page. */
  const goToPage = useCallback((p) => {
    patch({ page: p === 1 ? null : p }, { resetPage: false });
    const el = listRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - scrollOffset;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }, [patch, scrollOffset]);

  const clearAll = useCallback(() => {
    setTerm("");
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  return {
    items, extra, total, totalPages,
    page, perPage, perPageOptions, query,
    term, setTerm,
    state, refetching,
    goToPage, patch, reload: load, clearAll,
    listRef,
    isFiltered: Boolean(query) || perPage !== defaultPerPage,
  };
}

/* ── SearchField ─────────────────────────────────────────────────────────────
   The same control on every listing page: a search box that shows a spinner
   while a request is in flight and a clear button once there is something to
   clear. */
export function SearchField({ value, onChange, onClear, placeholder, busy, inputRef, clearLabel }) {
  return (
    <div className="field">
      <i className="bi bi-search" aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        dir="auto"
      />
      {busy && <span className="field-busy" aria-hidden="true" />}
      {value && (
        <button type="button" className="field-clear" onClick={onClear} aria-label={clearLabel}>
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

/* ── PerPageSelect ───────────────────────────────────────────────────────── */
export function PerPageSelect({ value, options, onChange, label }) {
  return (
    <div className="select">
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {options.map((n) => (
          <option key={n} value={n}>{n} {label}</option>
        ))}
      </select>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DETAIL PAGES — shared by article, page, service and chamber
   ═══════════════════════════════════════════════════════════════════════════ */

export const CACI_DETAIL_CSS = `
.caci-page .detail-wrap { display: grid; gap: clamp(32px, 5vw, 56px); }
@media (min-width: 992px) {
  .caci-page .detail-wrap {
    grid-template-columns: minmax(0, 1fr) 320px;
    align-items: start;
  }
}
.caci-page .detail-main { min-width: 0; }

/* The lead image is the one place a detail page can breathe. Capped by
   aspect-ratio rather than a fixed height so a portrait scan and a landscape
   photo both land in a predictable box. */
.caci-page .detail-hero {
  position: relative; overflow: hidden;
  border-radius: 20px; background: var(--mint);
  aspect-ratio: 16 / 9; margin-bottom: clamp(24px, 4vw, 40px);
  box-shadow: 0 30px 70px -34px rgba(8,21,14,.45);
}
.caci-page .detail-hero img { width: 100%; height: 100%; object-fit: cover; display: block; }
.caci-page .detail-hero::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.4);
  border-radius: 20px;
}

.caci-page .detail-meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px 18px;
  padding-block-end: 20px; margin-block-end: clamp(24px, 3.4vw, 34px);
  border-block-end: 1px solid var(--rule);
  font-family: var(--mono); font-size: 11.5px; letter-spacing: .06em;
  color: var(--muted);
}
.caci-page .detail-meta span { display: inline-flex; align-items: center; gap: 7px; }
.caci-page .detail-meta i { color: var(--brass); font-size: 12px; }

/* ── Prose ───────────────────────────────────────────────────────────────
   The article body arrives as HTML from the editor and was previously
   rendered with no typographic rules at all — inheriting whatever the
   Bootstrap theme happened to apply. This is the page's actual content, so
   it gets a real measure, real rhythm, and sane defaults for every element
   an editor can produce. */
.caci-page .prose {
  font-size: clamp(15.5px, 1.7vw, 17px);
  line-height: 1.78;
  color: var(--txt);
  max-width: 72ch;
  overflow-wrap: break-word;
}
.caci-page .prose > *:first-child { margin-block-start: 0; }
.caci-page .prose > *:last-child  { margin-block-end: 0; }
.caci-page .prose p { margin: 0 0 1.15em; }
.caci-page .prose h1, .caci-page .prose h2,
.caci-page .prose h3, .caci-page .prose h4 {
  font-family: var(--display); font-weight: 700;
  letter-spacing: -.026em; line-height: 1.18; color: var(--ink);
  margin: 1.9em 0 .55em; text-wrap: balance;
}
.caci-page .prose h1 { font-size: 1.75em; }
.caci-page .prose h2 { font-size: 1.45em; }
.caci-page .prose h3 { font-size: 1.2em; }
.caci-page .prose h4 { font-size: 1.05em; }
.caci-page .prose a {
  color: var(--pine); text-decoration: underline;
  text-underline-offset: 3px; text-decoration-thickness: 1px;
  text-decoration-color: rgba(15,74,48,.35);
  transition: text-decoration-color var(--t1) var(--e), color var(--t1) var(--e);
}
.caci-page .prose a:hover { color: var(--leaf); text-decoration-color: currentColor; }
.caci-page .prose strong, .caci-page .prose b { font-weight: 700; color: var(--ink); }
.caci-page .prose ul, .caci-page .prose ol { margin: 0 0 1.15em; padding-inline-start: 1.4em; }
.caci-page .prose li { margin-block-end: .45em; }
.caci-page .prose ul { list-style: none; padding-inline-start: 0; }
.caci-page .prose ul > li {
  position: relative; padding-inline-start: 1.5em;
}
.caci-page .prose ul > li::before {
  content: ''; position: absolute; inset-block-start: .62em; inset-inline-start: .3em;
  width: 6px; height: 6px; border-radius: 50%; background: var(--brass);
}
.caci-page .prose ul ul { margin-block: .45em 0; }
.caci-page .prose blockquote {
  margin: 1.6em 0; padding: 4px 0 4px 22px;
  border-inline-start: 3px solid var(--brass);
  font-size: 1.06em; line-height: 1.68; color: var(--pine);
}
.caci-page .prose blockquote p:last-child { margin-block-end: 0; }
.caci-page .prose img,
.caci-page .prose video,
.caci-page .prose iframe {
  display: block; max-width: 100%; height: auto;
  border-radius: 14px; margin: 1.6em 0;
}
.caci-page .prose iframe { width: 100%; aspect-ratio: 16 / 9; border: 0; }
.caci-page .prose figure { margin: 1.6em 0; }
.caci-page .prose figcaption {
  margin-block-start: .7em; font-size: .84em; color: var(--muted); text-align: center;
}
.caci-page .prose hr { border: 0; border-block-start: 1px solid var(--rule); margin: 2.2em 0; }
.caci-page .prose code {
  font-family: var(--mono); font-size: .88em;
  background: var(--mint); padding: .15em .45em; border-radius: 5px;
}
.caci-page .prose pre {
  background: var(--ink); color: #E9F3ED; padding: 18px;
  border-radius: 12px; overflow-x: auto; margin: 1.5em 0;
  font-family: var(--mono); font-size: .84em; line-height: 1.6;
}
.caci-page .prose pre code { background: none; padding: 0; color: inherit; }

/* Editor tables have no width control and used to run off the side of a
   phone. Wrapped so the table scrolls rather than the page. */
.caci-page .prose table {
  width: 100%; border-collapse: collapse; margin: 1.5em 0; font-size: .92em;
}
.caci-page .prose thead th {
  background: var(--mint); color: var(--pine);
  font-family: var(--mono); font-size: .82em; font-weight: 700;
  letter-spacing: .08em; text-transform: uppercase;
  text-align: start; padding: 12px 14px;
}
.caci-page .prose td, .caci-page .prose th {
  border: 1px solid var(--rule); padding: 11px 14px; vertical-align: top;
}
.caci-page .prose tbody tr:nth-child(even) { background: rgba(15,74,48,.022); }
/* Pure-CSS scroll shadows: the two radial gradients are attached to the
   scroll container while the two linear ones scroll with the content, so a
   shadow appears on an edge only while there is more table beyond it. Without
   this, a wide table just looked cut off with no hint it could be dragged. */
.caci-page .prose-scroll {
  overflow-x: auto; -webkit-overflow-scrolling: touch;
  background-image:
    linear-gradient(to right,  var(--paper), rgba(250,251,250,0)),
    linear-gradient(to left,   var(--paper), rgba(250,251,250,0)),
    radial-gradient(farthest-side at 0%   50%, rgba(8,21,14,.16), rgba(8,21,14,0)),
    radial-gradient(farthest-side at 100% 50%, rgba(8,21,14,.16), rgba(8,21,14,0));
  background-position: left center, right center, left center, right center;
  background-repeat: no-repeat;
  background-size: 34px 100%, 34px 100%, 13px 100%, 13px 100%;
  background-attachment: local, local, scroll, scroll;
}

/* ── Tags ─────────────────────────────────────────────────────────────── */
.caci-page .detail-tags {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  margin-block-start: clamp(30px, 4vw, 44px);
  padding-block-start: 22px; border-block-start: 1px solid var(--rule);
}
.caci-page .detail-tags-label {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: var(--muted);
  margin-inline-end: 4px;
}

/* ── Share ───────────────────────────────────────────────────────────────
   One share row, not two. The original rendered a full-width button row in
   the body and a duplicate icon row in the sidebar — the same six actions,
   twice on one screen. */
.caci-page .share {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  margin-block-start: clamp(30px, 4vw, 44px);
  padding-block-start: 24px; border-block-start: 1px solid var(--rule);
}
.caci-page .share-label {
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: var(--muted);
  margin-inline-end: 4px;
}
.caci-page .share-btn {
  width: 46px; height: 46px; border-radius: 50%; cursor: pointer;
  display: inline-grid; place-items: center; font-size: 16px;
  border: 1px solid var(--rule); background: var(--surface); color: var(--muted);
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-page .share-btn:hover { transform: translateY(-2px); color: #fff; }
.caci-page .share-btn:active { transform: translateY(0) scale(.94); }
.caci-page .share-btn.is-facebook:hover { background: #1877F2; border-color: #1877F2; }
.caci-page .share-btn.is-x:hover        { background: #0B0B0B; border-color: #0B0B0B; }
.caci-page .share-btn.is-linkedin:hover { background: #0A66C2; border-color: #0A66C2; }
.caci-page .share-btn.is-whatsapp:hover { background: #25D366; border-color: #25D366; }
.caci-page .share-btn.is-copy:hover     { background: var(--pine); border-color: var(--pine); }
.caci-page .share-btn.is-native {
  width: auto; padding-inline: 20px; gap: 9px; border-radius: 999px;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  background: var(--pine); border-color: var(--pine); color: #fff;
}
.caci-page .share-btn.is-native:hover { background: var(--leaf); border-color: var(--leaf); }
.caci-page .share-done {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: .08em; color: var(--leaf);
}

/* ── Sidebar ─────────────────────────────────────────────────────────── */
.caci-page .side { display: grid; gap: 18px; min-width: 0; }
@media (min-width: 992px) {
  .caci-page .side { position: sticky; inset-block-start: 22px; }
}
.caci-page .side-card {
  border: 1px solid var(--rule); border-radius: 18px;
  background: var(--surface); padding: 20px;
}
.caci-page .side-title {
  display: flex; align-items: center; gap: 9px;
  margin: 0 0 14px;
  font-family: var(--mono); font-size: 11px; font-weight: 700;
  letter-spacing: .14em; text-transform: uppercase; color: var(--pine);
}
.caci-page .side-title .seal { color: var(--brass); }
.caci-page .side-list { list-style: none; margin: 0; padding: 0; }
.caci-page .side-list li + li { border-block-start: 1px solid var(--rule); }
.caci-page .side-list a {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  min-height: 46px; padding-block: 6px;
  color: var(--txt); text-decoration: none; font-size: 14.5px;
  transition: color var(--t1) var(--e), padding-inline-start var(--t1) var(--e);
}
.caci-page .side-list a:hover { color: var(--pine); padding-inline-start: 6px; }
.caci-page .side-list a > span:first-child { min-width: 0; overflow-wrap: anywhere; }
.caci-page .side-count {
  flex: 0 0 auto; min-width: 30px; height: 24px; padding-inline: 8px;
  border-radius: 999px; background: var(--mint); color: var(--pine);
  display: inline-grid; place-items: center;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* ── Attachments ─────────────────────────────────────────────────────── */
.caci-page .files { display: grid; gap: 8px; }
.caci-page .file {
  display: flex; align-items: center; gap: 12px;
  min-height: 60px; padding: 10px 12px; border-radius: 12px;
  border: 1px solid var(--rule); background: var(--paper);
  text-decoration: none; color: inherit;
  transition: border-color var(--t1) var(--e), background var(--t1) var(--e),
              transform var(--t1) var(--e);
}
.caci-page .file:hover {
  border-color: rgba(176,138,46,.45); background: var(--surface); transform: translateY(-2px);
}
.caci-page .file-icon {
  flex: 0 0 auto; width: 38px; height: 38px; border-radius: 10px;
  display: grid; place-items: center; font-size: 17px;
  background: var(--mint); color: var(--pine);
}
.caci-page .file-text { min-width: 0; flex: 1; }
.caci-page .file-name {
  display: block; font-size: 14px; font-weight: 600; color: var(--ink);
  line-height: 1.35; overflow-wrap: anywhere;
}
.caci-page .file-kind {
  display: block; margin-block-start: 2px;
  font-family: var(--mono); font-size: 10px; letter-spacing: .1em;
  text-transform: uppercase; color: var(--muted);
}
.caci-page .file > i:last-child { flex: 0 0 auto; color: var(--muted); font-size: 14px; }

/* ── Detail skeleton ─────────────────────────────────────────────────── */
.caci-page .skel-detail { display: grid; gap: 14px; }
.caci-page .skel-detail .skel-hero { aspect-ratio: 16 / 9; border-radius: 20px; margin-block-end: 12px; }
.caci-page .skel-detail .skel-line { height: 15px; border-radius: 7px; }
.caci-page .skel-detail .skel-line.w1 { width: 96%; }
.caci-page .skel-detail .skel-line.w2 { width: 88%; }
.caci-page .skel-detail .skel-line.w3 { width: 62%; }
`;

/* Detail pages opt in with <CaciDetailStyles/> alongside <CaciStyles/>. */
export function CaciDetailStyles() {
  return <style>{CACI_DETAIL_CSS}</style>;
}

/* ── Prose ───────────────────────────────────────────────────────────────────
   CMS HTML, rendered with real typography. Tables get a scroll wrapper: an
   editor's table has no width control and used to push the whole page
   sideways on a phone. */
export function Prose({ html, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll("table").forEach((table) => {
      if (table.parentElement && table.parentElement.classList.contains("prose-scroll")) return;
      const wrap = document.createElement("div");
      wrap.className = "prose-scroll";
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
    /* Links inside editor content point at other sites often enough that
       opening them in place, with no rel, is the wrong default. */
    root.querySelectorAll('a[href^="http"]').forEach((a) => {
      if (a.hostname && a.hostname !== window.location.hostname) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
    });
  }, [html]);

  if (!html) return null;
  return (
    <div
      ref={ref}
      className={`prose ${className}`}
      dir="auto"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/* ── Share ───────────────────────────────────────────────────────────────────
   One row, not the two duplicated sets the originals rendered.

   On a phone the OS share sheet is offered first: it reaches every app the
   visitor actually has, including the ones no button row can cover. The
   explicit network buttons remain for desktop, where no share sheet exists. */
export function ShareBar({ url, title, label, copiedLabel, nativeLabel }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  /* window.navigator, not the bare global: Node and some bundler/SSR shims
     define their own read-only `navigator`, which shadows the browser one and
     makes this check silently report false. */
  const canNative =
    typeof window !== "undefined" &&
    typeof window.navigator?.share === "function" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;

  const flash = () => {
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2200);
  };

  const openPopup = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=620,height=480");
  };

  const shareNative = async () => {
    try {
      await window.navigator.share({ title, url });
    } catch (e) {
      /* AbortError just means the visitor dismissed the sheet. */
      if (e && e.name !== "AbortError") console.error("Share failed:", e);
    }
  };

  /* navigator.clipboard is undefined on insecure origins and in older
     browsers, where the original silently did nothing. Fall back to a
     hidden textarea so the button always does something. */
  const copy = async () => {
    const text = `${title}\n\n${url}`;
    try {
      if (window.navigator.clipboard && window.isSecureContext) {
        await window.navigator.clipboard.writeText(text);
        flash();
        return;
      }
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      flash();
    } catch (e) {
      console.error("Could not copy the link:", e);
    }
  };

  const u = encodeURIComponent(url);
  const tt = encodeURIComponent(title || "");

  return (
    <div className="share">
      <span className="share-label">{label}</span>

      {canNative && (
        <button type="button" className="share-btn is-native" onClick={shareNative}>
          <i className="bi bi-share-fill" />
          {nativeLabel}
        </button>
      )}

      <button
        type="button" className="share-btn is-facebook" aria-label="Facebook"
        onClick={() => openPopup(`https://www.facebook.com/sharer/sharer.php?u=${u}`)}
      >
        <i className="bi bi-facebook" />
      </button>
      <button
        type="button" className="share-btn is-x" aria-label="X"
        onClick={() => openPopup(`https://twitter.com/intent/tweet?url=${u}&text=${tt}`)}
      >
        <i className="bi bi-twitter-x" />
      </button>
      <button
        type="button" className="share-btn is-linkedin" aria-label="LinkedIn"
        onClick={() => openPopup(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`)}
      >
        <i className="bi bi-linkedin" />
      </button>
      <button
        type="button" className="share-btn is-whatsapp" aria-label="WhatsApp"
        onClick={() => openPopup(`https://api.whatsapp.com/send?text=${tt}%20${u}`)}
      >
        <i className="bi bi-whatsapp" />
      </button>
      <button
        type="button" className="share-btn is-copy" aria-label={label} onClick={copy}
      >
        <i className="bi bi-link-45deg" />
      </button>

      <span className="share-done" role="status" aria-live="polite">
        {copied && (<><i className="bi bi-check2" />{copiedLabel}</>)}
      </span>
    </div>
  );
}

/* ── Attachments ─────────────────────────────────────────────────────────── */
const FILE_ICONS = {
  pdf: "bi-file-earmark-pdf", doc: "bi-file-earmark-word", docx: "bi-file-earmark-word",
  xls: "bi-file-earmark-spreadsheet", xlsx: "bi-file-earmark-spreadsheet",
  csv: "bi-file-earmark-spreadsheet",
  ppt: "bi-file-earmark-slides", pptx: "bi-file-earmark-slides",
  jpg: "bi-file-earmark-image", jpeg: "bi-file-earmark-image",
  png: "bi-file-earmark-image", gif: "bi-file-earmark-image", webp: "bi-file-earmark-image",
  mp4: "bi-file-earmark-play", avi: "bi-file-earmark-play", mov: "bi-file-earmark-play",
  mp3: "bi-file-earmark-music", wav: "bi-file-earmark-music",
  zip: "bi-file-earmark-zip", rar: "bi-file-earmark-zip",
};

export function fileExtension(path) {
  const name = String(path || "").split("/").pop() || "";
  const dot = name.lastIndexOf(".");
  return dot > -1 ? name.slice(dot + 1).toLowerCase() : "";
}

export function fileIcon(path) {
  return FILE_ICONS[fileExtension(path)] || "bi-file-earmark";
}

export function fileName(path) {
  return decodeURIComponent(String(path || "").split("/").pop() || "");
}

/* Each row names the file and its type. The original showed only the
   description — which is frequently empty in the data — so some rows were an
   icon next to nothing at all. */
export function FileList({ files, baseUrl }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="files">
      {files.map((f, i) => {
        const path = f.name || "";
        const ext = fileExtension(path);
        const label = (f.description || "").trim() || fileName(path);
        return (
          <a
            key={f.id ?? i}
            className="file"
            // href={`${baseUrl}${path}`}
            href={/^https?:\/\//i.test(path) ? path : `${baseUrl}${path}`}
            download={fileName(path)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="file-icon"><i className={`bi ${fileIcon(path)}`} /></span>
            <span className="file-text">
              <span className="file-name" dir="auto">{label}</span>
              {ext && <span className="file-kind">{ext}</span>}
            </span>
            <i className="bi bi-download" />
          </a>
        );
      })}
    </div>
  );
}

/* ── Detail skeleton ─────────────────────────────────────────────────────── */
export function DetailSkeleton({ hero = true }) {
  return (
    <div className="skel-detail" aria-hidden="true">
      {hero && <Skel className="skel-hero" />}
      <Skel className="skel-line w1" />
      <Skel className="skel-line w2" />
      <Skel className="skel-line w1" />
      <Skel className="skel-line w3" />
      <Skel h={15} r={7} w="0" style={{ marginBlockStart: 14 }} />
      <Skel className="skel-line w2" />
      <Skel className="skel-line w1" />
      <Skel className="skel-line w3" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FOOTER
   Self-scoped under .caci-footer so it can sit under any page, migrated or
   not, without touching the Bootstrap theme.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CACI_FOOTER_CSS = `
.caci-footer {
  --ink: #08150E; --pine: #0F4A30; --leaf: #1A7A4A; --leaf-2: #22A060;
  --brass: #B08A2E; --brass-2: #D8B455;
  --display: 'Bricolage Grotesque', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --body: 'Instrument Sans', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --mono: 'JetBrains Mono', ui-monospace, monospace;
  --e: cubic-bezier(.22,1,.36,1); --t1: .26s;

  position: relative; isolation: isolate;
  background: var(--ink); color: rgba(255,255,255,.66);
  font-family: var(--body); overflow: hidden;
  padding-block: clamp(44px, 6vw, 76px) 0;
}
.caci-footer.is-rtl {
  --display: 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --body: 'IBM Plex Sans Arabic', system-ui, sans-serif;
}
.caci-footer *, .caci-footer *::before, .caci-footer *::after { box-sizing: border-box; }

/* The same engine-turn guilloché as the page headers, closing the frame. */
.caci-footer::before {
  content: ''; position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background:
    repeating-linear-gradient(114deg, rgba(216,180,85,.07) 0 1px, transparent 1px 9px),
    repeating-linear-gradient(-114deg, rgba(34,160,96,.06) 0 1px, transparent 1px 9px);
  -webkit-mask-image: radial-gradient(130% 90% at 50% 100%, #000 12%, transparent 76%);
          mask-image: radial-gradient(130% 90% at 50% 100%, #000 12%, transparent 76%);
}
.caci-footer .ft-shell {
  position: relative; z-index: 1;
  width: 100%; max-width: 1280px; margin-inline: auto;
  padding-inline: clamp(18px, 4.4vw, 40px);
}

.caci-footer .ft-grid { display: grid; gap: clamp(30px, 4vw, 44px); }
@media (min-width: 700px)  { .caci-footer .ft-grid { grid-template-columns: 1fr 1fr; } }
@media (min-width: 1100px) { .caci-footer .ft-grid { grid-template-columns: 1.25fr 1fr 1.35fr .85fr; } }
.caci-footer .ft-col { min-width: 0; }

.caci-footer .ft-logo { width: 96px; height: auto; margin-block-end: 14px; }
.caci-footer .ft-name {
  margin: 0 0 10px; font-family: var(--display);
  font-size: 20px; font-weight: 700; letter-spacing: -.024em; color: #fff;
}
.caci-footer .ft-desc {
  margin: 0; font-size: 14px; line-height: 1.72; max-width: 42ch;
  color: rgba(255,255,255,.55);
}

.caci-footer .ft-heading {
  display: inline-flex; align-items: center; gap: 9px; margin: 0 0 16px;
  font-family: var(--mono); font-size: 11px; font-weight: 700;
  letter-spacing: .16em; text-transform: uppercase; color: var(--brass-2);
}
.caci-footer .ft-heading::after {
  content: ''; width: 26px; height: 1px;
  background: linear-gradient(to right, rgba(216,180,85,.7), transparent);
}
.caci-footer.is-rtl .ft-heading::after {
  background: linear-gradient(to left, rgba(216,180,85,.7), transparent);
}

.caci-footer .ft-list { list-style: none; margin: 0; padding: 0; }
@media (min-width: 560px) {
  .caci-footer .ft-list-2 { columns: 2; column-gap: 22px; }
  .caci-footer .ft-list-2 li { break-inside: avoid; }
}
.caci-footer .ft-list a {
  display: flex; align-items: center; gap: 9px;
  min-height: 40px; padding-block: 4px;
  color: rgba(255,255,255,.66); text-decoration: none;
  font-size: 14px; line-height: 1.4;
  transition: color var(--t1) var(--e), padding-inline-start var(--t1) var(--e);
}
.caci-footer .ft-list a i {
  flex: 0 0 auto; font-size: 10px; color: var(--brass);
  opacity: .55; transition: opacity var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-footer .ft-list a:hover { color: #fff; padding-inline-start: 5px; }
.caci-footer .ft-list a:hover i { opacity: 1; transform: translateX(2px); }
.caci-footer.is-rtl .ft-list a:hover i { transform: translateX(-2px); }

.caci-footer .ft-social { display: flex; flex-wrap: wrap; gap: 9px; margin-block-end: 26px; }
.caci-footer .ft-social-btn {
  width: 44px; height: 44px; border-radius: 50%;
  display: inline-grid; place-items: center; font-size: 16px;
  border: 1px solid rgba(255,255,255,.16);
  background: rgba(255,255,255,.05); color: rgba(255,255,255,.72);
  text-decoration: none;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-footer .ft-social-btn:hover { transform: translateY(-3px); color: #fff; }
.caci-footer .ft-social-btn.is-facebook:hover  { background: #1877F2; border-color: #1877F2; }
.caci-footer .ft-social-btn.is-twitter:hover   { background: #0B0B0B; border-color: #3a3a3a; }
.caci-footer .ft-social-btn.is-instagram:hover { background: #C13584; border-color: #C13584; }
.caci-footer .ft-social-btn.is-linkedin:hover  { background: #0A66C2; border-color: #0A66C2; }

.caci-footer .ft-lang-label {
  display: flex; align-items: center; gap: 8px; margin-block-end: 9px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: rgba(255,255,255,.5);
}
.caci-footer .ft-lang { position: relative; display: flex; }
.caci-footer .ft-lang select {
  flex: 1; min-width: 0; min-height: 48px;
  padding-inline: 18px 44px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.05); color: #fff;
  font-family: var(--body); font-size: 14px; outline: none;
  -webkit-appearance: none; appearance: none;
  transition: border-color var(--t1) var(--e), background var(--t1) var(--e);
}
.caci-footer .ft-lang select:hover { border-color: rgba(216,180,85,.5); background: rgba(255,255,255,.08); }
.caci-footer .ft-lang select:focus-visible { outline: 3px solid var(--brass); outline-offset: 2px; }
/* The native menu renders in the OS palette; force readable colours. */
.caci-footer .ft-lang option { background: #12281C; color: #fff; }
/* Physical borders: a caret points down in every language. */
.caci-footer .ft-lang::after {
  content: ''; position: absolute; inset-inline-end: 20px; inset-block-start: 50%;
  width: 7px; height: 7px; pointer-events: none;
  border-right: 2px solid var(--brass-2); border-bottom: 2px solid var(--brass-2);
  transform: translateY(-70%) rotate(45deg);
}

.caci-footer .ft-base {
  margin-block-start: clamp(34px, 4.5vw, 54px);
  padding-block: 22px 26px;
  border-block-start: 1px solid rgba(255,255,255,.1);
  text-align: center;
}
.caci-footer .ft-copy {
  margin: 0; font-family: var(--mono); font-size: 11.5px;
  letter-spacing: .06em; color: rgba(255,255,255,.42);
}
.caci-footer .ft-copy strong { color: var(--brass-2); font-weight: 600; }

.caci-footer a:focus-visible, .caci-footer select:focus-visible {
  outline: 3px solid var(--brass); outline-offset: 3px; border-radius: 8px;
}
@media (prefers-reduced-motion: reduce) {
  .caci-footer *, .caci-footer *::before, .caci-footer *::after {
    transition-duration: .01ms !important; animation-duration: .01ms !important;
  }
}
`;

/* ═══════════════════════════════════════════════════════════════════════════
   HEADER — MENU BAR ONLY
   ───────────────────────────────────────────────────────────────────────────
   Scoped to .caci-navbar, which wraps ONLY the navigation strip. The
   institutional band above it (#header_center: logo, name, phone and e-mail)
   is deliberately left alone and keeps its existing markup and stylesheet —
   nothing here touches it.

   The bar is ink with brass accents, the same palette as CACI_FOOTER_CSS, so
   the white band on top and the dark bar underneath keep the rhythm the site
   already has while the menu itself moves onto the CaciUI design language.

   Tokens are declared locally because this lives outside .caci-page. Every
   class is new (hd-*), so no leftover rule for .header-container2 or
   .submenu-container can fight it.

   Not sticky: .caci-navbar is the last child of <header class="site-header">,
   so a sticky rule here would have almost no range to travel. To make the bar
   follow the scroll, add `position: sticky; inset-block-start: 0;` to
   .site-header in your own stylesheet — the z-index below already accounts
   for it.

   BREAKPOINT: the menu collapses into a drawer at 1100px. Header.js holds the
   same number in MOBILE_MAX — change both together.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CACI_HEADER_CSS = `
.caci-navbar {
  --ink: #08150E; --ink-2: #12281C; --pine: #0F4A30;
  --leaf: #1A7A4A; --leaf-2: #22A060;
  --brass: #B08A2E; --brass-2: #D8B455;
  --mint: #E9F3ED; --surface: #FFFFFF;
  --on: rgba(255,255,255,.82); --on-dim: rgba(255,255,255,.6);
  --hair: rgba(255,255,255,.14);
  --display: 'Bricolage Grotesque', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --body: 'Instrument Sans', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --mono: 'JetBrains Mono', ui-monospace, monospace;
  --e: cubic-bezier(.22,1,.36,1); --t1: .26s; --t2: .42s;
  --gut: clamp(14px, 3.4vw, 34px);

  position: relative; z-index: 900; isolation: isolate;
  background: var(--ink); color: var(--on);
  font-family: var(--body);
}
.caci-navbar *, .caci-navbar *::before, .caci-navbar *::after { box-sizing: border-box; }
.caci-navbar.is-rtl {
  --display: 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --body: 'IBM Plex Sans Arabic', system-ui, sans-serif;
}

/* The same engine-turn guilloché the footer and the page headers carry. */
.caci-navbar::before {
  content: ''; position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background:
    repeating-linear-gradient(114deg, rgba(216,180,85,.07) 0 1px, transparent 1px 9px),
    repeating-linear-gradient(-114deg, rgba(34,160,96,.06) 0 1px, transparent 1px 9px);
}
/* Gold thread separating the bar from the institutional band above it. */
.caci-navbar .hd-thread {
  position: relative; z-index: 1; height: 2px;
  background: linear-gradient(90deg, var(--leaf), var(--leaf-2) 34%, var(--brass-2) 70%, var(--brass));
}

.caci-navbar .hd-shell {
  position: relative; z-index: 1;
  width: 100%; max-width: 1280px; margin-inline: auto; padding-inline: var(--gut);
}
.caci-navbar .hd-bar-in { display: flex; align-items: center; gap: 10px; min-height: 56px; }

/* ── Menu ─────────────────────────────────────────────────────────────────── */
.caci-navbar .hd-nav { flex: 1 1 auto; min-width: 0; }
.caci-navbar .hd-menu {
  list-style: none; margin: 0; padding: 0;
  display: flex; align-items: center; gap: 1px; flex-wrap: wrap;
}
.caci-navbar .hd-item { position: relative; }
.caci-navbar .hd-node { display: flex; align-items: center; }

.caci-navbar .hd-link,
.caci-navbar .hd-trigger {
  position: relative;
  display: inline-flex; align-items: center; gap: 7px;
  min-height: 42px; padding-inline: 12px; border-radius: 10px;
  border: 0; background: none; cursor: pointer;
  font-family: var(--body); font-size: 14px; font-weight: 600;
  line-height: 1.3; color: var(--on); text-decoration: none;
  white-space: nowrap; text-align: start;
  transition: background var(--t1) var(--e), color var(--t1) var(--e);
}
.caci-navbar .hd-link:hover,
.caci-navbar .hd-trigger:hover,
.caci-navbar .hd-item.is-open > .hd-trigger,
.caci-navbar .hd-item.is-open > .hd-node > .hd-link {
  background: rgba(255,255,255,.09); color: #fff;
}
.caci-navbar .hd-link.is-current { color: #fff; }
.caci-navbar .hd-link.is-current::after {
  content: ''; position: absolute; inset-block-end: 5px;
  inset-inline-start: 12px; inset-inline-end: 12px;
  height: 2px; border-radius: 2px;
  background: linear-gradient(90deg, var(--brass), var(--brass-2));
}
/* A CMS item with no real target: readable, but not pretending to be a link. */
.caci-navbar .hd-link.is-static { cursor: default; color: var(--on-dim); }
.caci-navbar .hd-link.is-static:hover { background: none; color: var(--on-dim); }

.caci-navbar .hd-trigger i,
.caci-navbar .hd-caret i {
  flex: 0 0 auto; font-size: 10px; color: var(--brass-2);
  transition: transform var(--t1) var(--e);
}
.caci-navbar .hd-item.is-open > .hd-trigger i,
.caci-navbar .hd-item.is-open > .hd-node > .hd-caret i { transform: rotate(180deg); }

/* A parent with a page of its own gets a real link plus a separate caret,
   instead of the label being swallowed by the toggle. */
.caci-navbar .hd-caret {
  display: inline-grid; place-items: center;
  width: 28px; min-height: 42px; border: 0; background: none;
  cursor: pointer; border-radius: 10px;
  transition: background var(--t1) var(--e);
}
.caci-navbar .hd-caret:hover { background: rgba(255,255,255,.09); }

/* ── Dropdowns ─────────────────────────────────────────────────────────────── */
.caci-navbar .hd-sub {
  position: absolute; inset-block-start: calc(100% + 4px); inset-inline-start: 0;
  z-index: 40; min-width: 246px; max-width: 330px; padding: 8px;
  border: 1px solid var(--hair); border-radius: 14px;
  background: var(--ink-2);
  box-shadow: 0 34px 64px -28px rgba(0,0,0,.65);
  opacity: 0; visibility: hidden; transform: translateY(-7px);
  transition: opacity var(--t1) var(--e), transform var(--t1) var(--e),
              visibility var(--t1) var(--e);
}
.caci-navbar .hd-item.is-open > .hd-sub { opacity: 1; visibility: visible; transform: none; }
.caci-navbar .hd-sub ul { list-style: none; margin: 0; padding: 0; }
.caci-navbar .hd-sub .hd-link,
.caci-navbar .hd-sub .hd-trigger {
  display: flex; width: 100%; justify-content: space-between;
  min-height: 42px; border-radius: 9px;
  font-size: 13.5px; font-weight: 500; white-space: normal;
  color: rgba(255,255,255,.76);
}
.caci-navbar .hd-sub .hd-node { width: 100%; }
.caci-navbar .hd-sub .hd-node .hd-link { flex: 1; }
.caci-navbar .hd-sub .hd-link.is-current::after { content: none; }
.caci-navbar .hd-sub .hd-link.is-current { background: rgba(216,180,85,.16); color: #fff; }
/* Second level flies out sideways. */
.caci-navbar .hd-sub .hd-sub { inset-block-start: -9px; inset-inline-start: calc(100% + 4px); }
/* The rightmost menus would otherwise open off the edge of the viewport. */
.caci-navbar .hd-menu > .hd-item:nth-last-child(-n+3) > .hd-sub {
  inset-inline-start: auto; inset-inline-end: 0;
}

/* ── Tools ─────────────────────────────────────────────────────────────────── */
.caci-navbar .hd-tools { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.caci-navbar .hd-icon-btn {
  width: 40px; height: 40px; border-radius: 11px;
  display: grid; place-items: center; cursor: pointer;
  border: 1px solid var(--hair); background: rgba(255,255,255,.05);
  color: rgba(255,255,255,.82); font-size: 16px;
  transition: background var(--t1) var(--e), border-color var(--t1) var(--e),
              color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-navbar .hd-icon-btn:hover {
  background: rgba(255,255,255,.11); border-color: rgba(216,180,85,.45); color: #fff;
}
.caci-navbar .hd-icon-btn:active { transform: scale(.95); }
.caci-navbar .hd-icon-btn.is-on {
  background: var(--brass); border-color: var(--brass); color: var(--ink);
}
.caci-navbar .hd-burger { display: none; }

.caci-navbar .hd-lang { position: relative; }
.caci-navbar .hd-lang-btn {
  display: inline-flex; align-items: center; gap: 7px;
  min-height: 40px; padding-inline: 13px; border-radius: 999px; cursor: pointer;
  border: 1px solid var(--hair); background: rgba(255,255,255,.05);
  color: rgba(255,255,255,.86);
  font-family: var(--mono); font-size: 11.5px; font-weight: 700;
  letter-spacing: .08em; text-transform: uppercase;
  transition: background var(--t1) var(--e), border-color var(--t1) var(--e);
}
.caci-navbar .hd-lang-btn i { font-size: 13px; color: var(--brass-2); }
.caci-navbar .hd-lang-btn:hover {
  background: rgba(255,255,255,.11); border-color: rgba(216,180,85,.45);
}
.caci-navbar .hd-lang-menu {
  position: absolute; inset-block-start: calc(100% + 7px); inset-inline-end: 0;
  z-index: 50; min-width: 172px; padding: 6px;
  border: 1px solid var(--hair); border-radius: 13px; background: var(--ink-2);
  box-shadow: 0 30px 58px -26px rgba(0,0,0,.65);
}
.caci-navbar .hd-lang-opt {
  display: flex; align-items: center; gap: 10px; width: 100%;
  min-height: 42px; padding-inline: 12px; border-radius: 9px;
  border: 0; background: none; cursor: pointer; text-align: start;
  font-family: var(--body); font-size: 14px; color: rgba(255,255,255,.78);
  transition: background var(--t1) var(--e), color var(--t1) var(--e);
}
.caci-navbar .hd-lang-opt:hover { background: rgba(255,255,255,.09); color: #fff; }
.caci-navbar .hd-lang-opt.is-active {
  background: rgba(216,180,85,.16); color: #fff; font-weight: 600;
}
.caci-navbar .hd-lang-opt .bi-check2 { margin-inline-start: auto; color: var(--brass-2); }

/* ── Search panel ──────────────────────────────────────────────────────────
   Overlays the page rather than pushing it down, and closes on Escape. */
.caci-navbar .hd-search {
  position: absolute; inset-block-start: 100%; inset-inline: 0; z-index: 60;
  padding-block: clamp(13px, 1.8vw, 18px);
  background: var(--ink-2); border-block-end: 1px solid var(--hair);
  box-shadow: 0 30px 58px -32px rgba(0,0,0,.7);
}
.caci-navbar .hd-search form { display: flex; align-items: center; gap: 10px; }
.caci-navbar .hd-search input {
  flex: 1; min-width: 0; min-height: 50px;
  padding-inline: 20px; border-radius: 999px;
  border: 1px solid var(--hair); background: rgba(255,255,255,.07); color: #fff;
  font-family: var(--body); font-size: 15px; outline: none;
  transition: border-color var(--t1) var(--e), background var(--t1) var(--e);
}
.caci-navbar .hd-search input::placeholder { color: rgba(255,255,255,.45); }
.caci-navbar .hd-search input:focus {
  border-color: rgba(216,180,85,.55); background: rgba(255,255,255,.1);
}
.caci-navbar .hd-search-go {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 9px;
  min-height: 50px; padding-inline: 22px; border-radius: 999px;
  border: 0; cursor: pointer; color: #fff;
  background: linear-gradient(180deg, var(--leaf-2), var(--leaf));
  font-family: var(--body); font-size: 14.5px; font-weight: 600;
  box-shadow: 0 14px 30px -16px rgba(26,122,74,.9);
  transition: transform var(--t1) var(--e);
}
.caci-navbar .hd-search-go:hover { transform: translateY(-2px); }
.caci-navbar .hd-search-go:active { transform: translateY(0) scale(.98); }

.caci-navbar .hd-drawer-meta { display: none; }

/* ── Focus ─────────────────────────────────────────────────────────────────── */
.caci-navbar a:focus-visible,
.caci-navbar button:focus-visible,
.caci-navbar input:focus-visible {
  outline: 3px solid var(--brass-2); outline-offset: 2px; border-radius: 10px;
}

/* ══════════════════════════════════════════════════════════════════════════
   COLLAPSED MENU — 1100px and below.
   Header.js holds the same number in MOBILE_MAX; change both together.
   ══════════════════════════════════════════════════════════════════════════ */
@media (max-width: 1100px) {
  .caci-navbar .hd-burger { display: grid; order: -1; }
  .caci-navbar .hd-tools { width: 100%; }
  .caci-navbar .hd-lang { margin-inline-start: auto; }

  /* Absolute, not fixed: the drawer overlays the page instead of shoving it
     down, and .caci-navbar is its containing block. */
  .caci-navbar .hd-nav {
    position: absolute; inset-block-start: 100%; inset-inline: 0; z-index: 55;
    display: none;
    max-height: min(72vh, 620px); overflow-y: auto; overscroll-behavior: contain;
    padding: 10px var(--gut) 22px;
    background: var(--ink-2); border-block-end: 1px solid var(--hair);
    box-shadow: 0 30px 58px -32px rgba(0,0,0,.7);
  }
  .caci-navbar.is-open .hd-nav { display: block; }

  .caci-navbar .hd-menu { flex-direction: column; align-items: stretch; gap: 2px; }
  .caci-navbar .hd-item { position: static; }
  .caci-navbar .hd-node { justify-content: space-between; }
  .caci-navbar .hd-link,
  .caci-navbar .hd-trigger {
    width: 100%; justify-content: space-between; min-height: 50px;
    font-size: 15px; white-space: normal;
  }
  .caci-navbar .hd-node > .hd-link { width: auto; flex: 1; }
  .caci-navbar .hd-caret { width: 44px; }
  .caci-navbar .hd-link.is-current::after { inset-block-end: 7px; }

  .caci-navbar .hd-sub {
    position: static; display: none;
    opacity: 1; visibility: visible; transform: none;
    min-width: 0; max-width: none; padding: 2px 0 6px;
    border: 0; border-radius: 0; box-shadow: none; background: none;
    margin-inline-start: 13px;
    border-inline-start: 2px solid var(--hair);
  }
  .caci-navbar .hd-item.is-open > .hd-sub { display: block; }
  .caci-navbar .hd-sub .hd-link,
  .caci-navbar .hd-sub .hd-trigger { padding-inline-start: 15px; font-size: 14px; }
  .caci-navbar .hd-sub .hd-sub { inset-inline-start: auto; }

  /* The band above hides its contact column on small screens (hidden-xs), so
     the phone and e-mail are repeated at the foot of the drawer. */
  .caci-navbar .hd-drawer-meta {
    display: grid; gap: 2px;
    margin-block-start: 14px; padding-block-start: 14px;
    border-block-start: 1px solid var(--hair);
  }
  .caci-navbar .hd-drawer-meta a,
  .caci-navbar .hd-drawer-meta span {
    display: flex; align-items: center; gap: 10px; min-height: 40px;
    color: rgba(255,255,255,.66); text-decoration: none; font-size: 13.5px;
  }
  .caci-navbar .hd-drawer-meta i { color: var(--brass-2); font-size: 15px; }
  .caci-navbar .hd-drawer-meta a:hover { color: #fff; }
}

@media (max-width: 560px) {
  .caci-navbar .hd-bar-in { min-height: 54px; }
  .caci-navbar .hd-search form { flex-wrap: wrap; }
  .caci-navbar .hd-search-go { flex: 1; justify-content: center; }
  .caci-navbar .hd-lang-btn { padding-inline: 11px; }
  .caci-navbar .hd-lang-btn span { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .caci-navbar *, .caci-navbar *::before, .caci-navbar *::after {
    transition-duration: .01ms !important; animation-duration: .01ms !important;
  }
}
`;
